import React, { useState, useRef } from 'react';
import ReactDOM from "react-dom";

export const demoBlogs = [
    { 
        id: 17, 
        title: "Playing Data with Python", 
        category: "Tech", 
        date: "2026", 
        author: "System_K31",
        content: [
    
            <p key="intro" className="mb-6 text-slate-300">
                Data Science with Python is not only about building machine learning models. 
                It is a complete workflow that starts from collecting raw data, processing it, 
                analyzing patterns, and finally building intelligent systems.
            </p>,
    
            <p key="intro2" className="mb-8 text-slate-300">
                In this guide, I will walk through the fundamental steps used in a typical 
                Python data science workflow. Each step also includes a video explanation 
                for understanding.
            </p>,
    
            <h2 key="steps-title" className="text-xl font-semibold mb-4 text-white">
                Data Science Workflow
            </h2>,
    
            <ul key="steps" className="space-y-3 mb-8 text-slate-300">
    
                <li key="s1">
                    <a href="https://www.youtube.com/embed/FdFOZraKAjU?si=_izVNvCg06Jdkkr-"
                    className="text-red-400 hover:underline">
                    1. Web Scraping
                    </a>
                    <span className="text-slate-400"> — Collecting raw data from websites.</span>
                </li>
    
                <li key="s2">
                    <a href="https://www.youtube.com/embed/y-lnFDpefAg?si=xf-qxWGD34buRdfC"
                    className="text-red-400 hover:underline">
                    2. Filtering Data
                    </a>
                    <span className="text-slate-400"> — Selecting only relevant rows based on conditions.</span>
                </li>
    
                <li key="s3">
                    <a href="#"
                    className="text-red-400 hover:underline">
                    3. Data Extraction
                    </a>
                    <span className="text-slate-400"> — Extracting specific information or columns from datasets.</span>
                </li>
    
                <li key="s4">
                    <a href="#"
                    className="text-red-400 hover:underline">
                    4. Reshaping and Pivoting DataFrames
                    </a>
                    <span className="text-slate-400"> — Transforming data structures for better analysis.</span>
                </li>
    
                <li key="s5">
                    <a href="#"
                    className="text-red-400 hover:underline">
                    5. Making Data Visualizations
                    </a>
                    <span className="text-slate-400"> — Creating charts to understand patterns and trends.</span>
                </li>
    
                <li key="s6">
                    <a href="#"
                    className="text-red-400 hover:underline">
                    6. GroupBy and Aggregate Functions
                    </a>
                    <span className="text-slate-400"> — Summarizing data by categories.</span>
                </li>
    
                <li key="s7">
                    <a href="#"
                    className="text-red-400 hover:underline">
                    7. Merging and Concatenating DataFrames
                    </a>
                    <span className="text-slate-400"> — Combining multiple datasets together.</span>
                </li>
    
                <li key="s8">
                    <a href="#"
                    className="text-red-400 hover:underline">
                    8. Regular Expressions
                    </a>
                    <span className="text-slate-400"> — Pattern matching for text processing.</span>
                </li>
    
                <li key="s9">
                    <a href="#"
                    className="text-red-400 hover:underline">
                    9. Data Cleaning
                    </a>
                    <span className="text-slate-400"> — Handling missing values, duplicates, and messy data.</span>
                </li>
    
                <li key="s10">
                    <a href="#"
                    className="text-red-400 hover:underline">
                    10. Machine Learning
                    </a>
                    <span className="text-slate-400"> — Training models to learn patterns from data.</span>
                </li>
    
                <li key="s11">
                    <a href="#"
                    className="text-red-400 hover:underline">
                    11. Text Classification with scikit-learn
                    </a>
                    <span className="text-slate-400"> — Using NLP techniques to classify text data.</span>
                </li>
    
            </ul>,
    
            <p key="closing" className="text-slate-300">
                Each step in this workflow represents a crucial skill in modern data science. 
                By mastering these concepts using Python libraries such as Pandas, NumPy, 
                Matplotlib, and scikit-learn, you can build powerful data driven solutions.
            </p>
    
        ],        
        image: "/kuma.jpg",
    },
    { 
        id: 16, 
        title: "Synergy in Solitude", 
        category: "Opinion", 
        date: "2025", 
        author: "System_K31",
        content: [           
            <p key="intro1" className="mb-6">
                Achieving monumental goals always presents the dual challenge of solitude and synergy. 
                There are moments we must move like a Pawn in Chess, advancing one step at a time, 
                relying purely on tenacity and principle. This path is bounded by our own limitations, 
                our energy, our vision, and our finite time. The power of one is vast yet constrained.
            </p>,
        
            <p key="intro2" className="mb-6">
                But to truly checkmate the King of complex problems we must transcend those personal boundaries. 
                We need the entire army of Rooks, Knights, and the Queen moving in perfect synergy. 
                The collective power is exponential and capable of achieving feats far greater 
                than the sum of its brilliant individuals.
            </p>,
        
            <p key="intro3" className="mb-6">
                Life is also like Igo or Baduk. A stone that stands alone will eventually fall 
                limited by its few liberties. True and unbounded strength emerges from a network of connected stones 
                where the group harmony overcomes the fragility of the single unit. 
                The key to accessing this collective power is not in sheer numbers 
                but in three foundational pillars that must be built before any real commitment can take root.
            </p>,
        
            <h2 key="pillar-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                The Three Foundational Pillars
            </h2>,       
            <h3 key="principle-title" className="text-xl font-semibold text-purple-300 mt-8 mb-4">
                Principle : Self Sovereignty and the Irreplaceable King
            </h3>,
            <p key="principle1" className="mb-6">
                Principle is the King. It is not a strategy but a form of self sovereignty. 
                It is the non negotiable standard that governs our reason and purpose.
            </p>,
            <p key="principle2" className="mb-6">
                In Chess the King must be protected at all costs. 
                Principle is the moral boundary we will not cross 
                even when short term temptations offer great rewards.
            </p>,
            <p key="principle3" className="mb-8">
                A person who easily shifts their principle for comfort exposes their King and loses their center. 
                Principle ensures that any alliance remains rooted in integrity. 
                The worst defeat is not losing the game but losing the sovereignty of one principle.
            </p>,
            <img
                key="pillar-img"
                alt="chess"
                src="/Blog_01_Synergy_Baduk.jpg"
                className="w-full max-w-2xl  object-contain rounded-lg shadow-md  mb-6"
            />,          
            <h3 key="trust-title" className="text-xl font-semibold text-purple-300 mt-8 mb-4">
                Trust : The Collective Breath and the Living Eye
            </h3>,
            <p key="trust1" className="mb-6">
                Trust is the Eye in Igo. It is not about data or verification 
                but the shared breath that keeps the whole alive.
            </p>,
            <p key="trust2" className="mb-6">
                In Igo the Eye is empty space that allows a group of stones to live. 
                Trust is that space between individuals built through transparency and consistency 
                where each soul can breathe and contribute without fear of betrayal.
            </p>,
            <p key="trust3" className="mb-8">
                Trust is the living pulse of an alliance. 
                It gives courage to take risks and endure temporary loss 
                because belief in the shared purpose stays intact. 
                A single deception is like closing the group only Eye and the structure suffocates.
            </p>,
        
            <h3 key="respect-title" className="text-xl font-semibold text-purple-300 mt-8 mb-4">
                Respect : Mutual Acknowledgment and the Value of Every Piece
            </h3>,
            <p key="respect1" className="mb-6">
                Respect is acknowledgment of value. 
                It ensures that every piece from the powerful Queen to the humble Pawn 
                is treated with dignity and awareness of its role.
            </p>,
            <p key="respect2" className="mb-6">
                In Chess respect means understanding the unique value and movement of every piece 
                even the opponent. A Pawn if ignored may rise to become a Queen. 
                Respect prevents reckless decisions and careless disregard for a partner perspective.
            </p>,
            <p key="respect3" className="mb-8">
                Respect is the social contract that defines our interactions. 
                It keeps communication constructive and boundaries honored. 
                An alliance without respect is only a tactical deal waiting to collapse.
            </p>,
            <img
                key="pillar-img"
                alt="chess"
                src="/Blog_01_Synergy_Chess.jpg"
                className="w-full max-w-2xl  object-contain rounded-lg shadow-md  mb-6"
            />,
            <h2 key="loyalty-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                The Ultimate Outcome : Loyalty
            </h2>,
            <p key="loyalty1" className="mb-6">
                Loyalty is not a pillar but a fortress built upon them. 
                It is the outcome of principle, trust, and respect practiced over time. 
                It is the unwavering defense that transforms individuals into a united force.
            </p>,
            <p key="loyalty2" className="mb-6">
                The King sets the moral direction, 
                the Eye maintains open communication, 
                and every piece contributes its strength. 
                From these loyalty emerges as the foundation that endures both victory and hardship.
            </p>,
            <p key="loyalty3" className="mb-8">
                True loyalty is what allows a team to achieve Checkmate 
                or survive the prolonged struggle of the Ko without falling apart.
            </p>,
        
            <h2 key="reality-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                The Final Dilemma : The Gap Between Ideal and Reality
            </h2>,
            <p key="reality1" className="mb-6">
                Building a collective based on principle trust and respect is never easy. 
                The ideal is clear but the human reality is complex. 
                Ego fear and conflicting values often make the construction of this fortress painful. 
                Many times, the journey resembles the Pawn's lonely path, not as a matter of preference, 
                but as a reflection of how rare genuine synergy founded on Principle, Trust, and Respect can be. 
                Yet that rarity is precisely what makes such alliances worth building, for those founded on these pillars possess the resilience to endure both victory and adversity.
            </p>,
            <p key="reality2" className="mb-8">
                This tension between the need for unity and the difficulty of its creation 
                defines the hardest part of this concept.
            </p>,
        
            <h2 key="warning-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                The Warning : The Questionable Foundation
            </h2>,
            <p key="warning1" className="mb-6">
                When we evaluate any relationship we must look beyond goals 
                and examine the integrity of its base.
            </p>,
            <ul key="warning-list" className="list-disc list-inside mb-8 space-y-2 text-blue-200">
                <li>Without Principle the alliance is a strategy without direction</li>
                <li>Without Trust the alliance is an organism that cannot breathe</li>
                <li>Without Respect the alliance becomes a fragile structure waiting to fracture</li>
            </ul>,
        
            <p key="closing" className="mb-10">
                True Checkmate in life belongs only to those 
                who build their foundation on Principle, Trust, and Respect. 
                The unshakable triad that sustains every alliance through both victory and chaos.
            </p>
        ],        
        image: "/Blog_01_Synergy.png",
    },
    { 
        id: 15, 
        title: "Tactics, Strategy, Chaos", 
        category: "Opinion", 
        date: "2025", 
        author: "System_K31",
        content: [
            <p key="intro1" className="mb-6">
                Life often feels like a vast game, sometimes a puzzle demanding precise
                solutions, other times an unpredictable voyage where all we have is a
                compass and intuition. Two of humanity’s oldest games, <strong>Chess</strong> and{" "}
                <strong>Go</strong>, illuminate how we navigate this spectrum between
                clarity and chaos. They teach us three essential dimensions of the mind:
                <strong> Tactics</strong>, <strong>Strategy</strong>, and{" "}
                <strong>Chaos</strong>.
            </p>,

            <ul key="core-3" className="list-disc list-inside mb-6 space-y-2 text-blue-200">
                <li>
                    <strong>Tactics</strong> is the art of winning battles : precision,
                    timing, and the immediate exchange of power.
                </li>
                <li>
                    <strong>Strategy</strong> is the art of winning wars : vision,
                    adaptation, and patience to shape a long game.
                </li>
                <li>
                    <strong>Chaos</strong> is the bridge : a realm where plans and resilience collide with reality.
                </li>
            </ul>,

            <p key="intro2" className="mb-8">
                Let us explore how Chess and Go teach not only the art of planning, but also the grace of surviving disorder.
            </p>,
            <img
                key="pillar-img"
                alt="chess"
                src="/Blog_02_Chaos_Chess.jpg"
                className="w-full max-w-2xl  object-contain rounded-lg shadow-md  mb-6"
            />,
            <h2 key="chess-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                Chess : Meditation in Mortal Order
            </h2>,

            <p key="chess1" className="mb-6">
                When I seek focus, I turn to <strong>Chess</strong>. Within its rigid geometry
                lies a mirror of disciplined thought, a labyrinth of logic where every
                move reverberates through the entire board. In my experience the journey of the mind in
                Chess unfolds in four layers :
            </p>,

            <ul key="chess-layers" className="list-decimal list-inside mb-6 space-y-3 text-blue-200">
                <li>
                    <strong>Focus on Survival :</strong> The novice learns restraint, how not
                    to fall apart. studies the value of each piece, builds defenses,
                    and learns that endurance is the first form of wisdom.
                </li>
                <li>
                    <strong>Focus on Combination :</strong> Then comes the joy of linking
                    ideas. Moves form chains, traps, ripples, forks, sacrifices, and forced
                    mates. The player begins to see not one move ahead, but several, like
                    glimpsing time itself unfolding.
                </li>
                <li>
                    <strong>Focus on Control :</strong> Here, the game turns abstract. One
                    stops fixating on pieces and begins to command space. You no longer
                    react, you orchestrate. The focus shifts from tactics to narrative, the story of a position becoming inevitable.
                </li>
                <li>
                    <strong>Focus on Flow :</strong> At the highest level, calculation and
                    intuition fuse. You read the pulse of your opponent, the rhythm beneath
                    their moves. The game ceases to be a contest of logic and becomes a
                    dialogue of presence.
                </li>
            </ul>,

            <p key="chess-end" className="mb-10">
                Why is Chess the emblem of focus ? Because it is a <strong>closed system</strong>.
                Every question has an optimal answer. The task is to find the needle of
                truth buried in a haystack of possibilities, a meditation on clarity
                itself.
            </p>,
            <img
                key="pillar-img"
                alt="chess"
                src="/Blog_02_Chaos_Baduk.jpg"
                className="w-full max-w-2xl  object-contain rounded-lg shadow-md  mb-6"
            />,
            <h2 key="go-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                Go : The Canvas of Strategic Imagination
            </h2>,

            <p key="go1" className="mb-6">
                When I crave creativity, I turn to <strong>Go</strong>. If Chess is a
                labyrinth, Go is a universe. The number of possible board states exceeds
                the atoms in existence. Victory here is not about killing a king, but
                shaping influence, claiming balance and territory through quiet
                expansion. Each stone is a brushstroke on an infinite canvas.
            </p>,

            <p key="go2" className="mb-6">
                Go reveals the soul of its player. You may attack boldly, build patient
                walls, or even sacrifice one realm to rule another. It’s not a game of
                domination, but of <strong>harmony through tension</strong>.
            </p>,

            <p key="go3" className="mb-6">
                Creativity in Go is not an option, it’s a necessity. You cannot compute
                the infinite, you must imagine it. You must dream shapes, sense the
                invisible, and trust the intuition that precedes reason. It is a game for
                those who see potential where nothing yet exists, like a visionary
                building a city upon emptiness.
            </p>,

            <p key="go4" className="mb-10">
                Why is Go the emblem of creativity ? Because it offers{" "}
                <strong>absolute freedom</strong> from the first move. It teaches comfort
                in uncertainty and faith in long term vision.
            </p>,

            <h2 key="chaos-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                Training the Mind in Chaos
            </h2>,

            <p key="chaos1" className="mb-6">
                And where, then, do we learn to face chaos ? Within these very games.
            </p>,

            <p key="chaos2" className="mb-6">
                To play them is to live through miniature worlds, worlds where order and
                disorder continuously intertwine. In Chess, chaos arises from{" "}
                <strong>complexity</strong>, a battlefield of clarity tested by time
                pressure and human error. In Go, chaos emerges from{" "}
                <strong>freedom</strong>, the boundless possibility that demands
                intuition to tame it.
            </p>,

            <p key="chaos3" className="mb-6">
                In them, we do not merely learn to plan. We learn to{" "}
                <strong>let go</strong>. To see the beauty of chaos not as an enemy of
                reason, but as its greatest teacher.
            </p>
        ],
        image: "/Blog_02_Chaos.png",
    },
    { 
        id: 14, 
        title: "The Hierarchy of Perception", 
        category: "Philosophy", 
        date: "2026", 
        author: "System_K31",
        content: [
            <blockquote key="intro-quote" className="italic border-l-4 border-blue-400 pl-4 mb-6 text-blue-200">
                "The world is never limited by what exists. It is limited by what we are capable of perceiving."
            </blockquote>,
    
            <p key="intro1" className="mb-6">
                For centuries, humanity has been obsessed with intelligence. We built IQ tests, measured memory, rewarded calculation, and admired those who could answer difficult questions.
            </p>,
    
            <p key="intro2" className="mb-6">
                Yet history keeps playing the same trick, the people who change the world are rarely the ones who merely know more. <strong>They are the ones who see differently.</strong>
            </p>,
    
            <p key="intro3" className="mb-6">
                Perhaps intelligence has been the wrong question all along. Perhaps <strong>perception</strong> is the true currency of civilization.
            </p>,
    
            <p key="intro4" className="mb-8">
                To understand how history is made, we must look at the invisible ladder of human sight, a journey that ascends layer by layer, moving from simple awareness to the ultimate redesign of reality.
            </p>,
    
            <h2 key="step1-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                Step I: Observation
            </h2>,
    
            <p key="step1-p1" className="mb-4">
                It begins at the ground floor, <strong>Observation</strong>. Everyone sees, but very few truly observe. Observation is the quiet art of collecting facts before judgment steps in.
            </p>,
    
            <ul key="step1-list" className="list-disc list-inside mb-6 space-y-2 text-blue-200">
                <li>A machine begins to vibrate differently.</li>
                <li>Birds disappear from their usual nesting place.</li>
                <li>A close friend grows quiet.</li>
            </ul>,
    
            <p key="step1-p2" className="mb-8">
                Most people skip this baseline entirely. They do not observe reality as it is, they observe what they <em>expect</em> it to be. But expectation is a blindfold, and raw observation is the first step toward clarity.
            </p>,
    
            <h2 key="step2-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                Step II: Pattern
            </h2>,
    
            <p key="step2-p1" className="mb-6">
                Yet, an isolated fact means almost nothing. The true shift happens when scattered observations begin to collide and form a <strong>Pattern</strong>.
            </p>,
    
            <p key="step2-p2" className="mb-6">
                The apple did not inspire Isaac Newton simply because it fell, apples had been falling for thousands of years. What changed was not gravity, what changed was Newton's frame of reference. Where others saw a random, everyday coincidence, he saw a recurring principle.
            </p>,
    
            <h2 key="step3-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                Step III: Structure
            </h2>,
    
            <p key="step3-p1" className="mb-4">
                Recognizing a pattern grants insight, but it still doesn't reveal <em>why</em> the repetition exists. To master the pattern, perception must plunge deeper into <strong>Structure</strong>.
            </p>,
    
            <p key="step3-p2" className="mb-4">
                At this depth, the visible world reveals itself as merely the shadow of unseen systems:
            </p>,
    
            <ul key="step3-list" className="list-disc list-inside mb-6 space-y-2 text-blue-200">
                <li>
                    <strong>A physician</strong> no longer fights a fever, but identifies the underlying infection.
                </li>
                <li>
                    <strong>An urban planner</strong> no longer builds more roads, but redesigns the transportation system that shapes traffic.
                </li>
                <li>
                    <strong>An engineer</strong> no longer repairs the same failure, but redesigns the system that keeps producing it.
                </li>
            </ul>,
    
            <p key="step3-p3" className="mb-8">
                While the majority spends its energy fighting surface symptoms, a rare few look underneath to redesign the system itself.
            </p>,
    
            <h2 key="step4-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                Step IV: Possibility
            </h2>,
    
            <p key="step4-p1" className="mb-6">
                Once you map the hidden structure, perception achieves a miraculous break, it stops merely describing reality and begins escaping it. It enters the realm of <strong>Possibility</strong>.
            </p>,
    
            <p key="step4-p2" className="mb-6">
                When the Wright brothers watched birds in flight, they didn't just admire them like everyone else. They asked a fundamental question that bypassed existing logic, <em>"What hidden principle makes flight possible?"</em>
            </p>,
    
            <p key="step4-p3" className="mb-8">
                The average mind asks, <em>"What is?"</em> The extraordinary mind asks, <strong>"What else could be?"</strong> This is perception completely liberated from habit, the realization that present reality is not a boundary, but a starting line.
            </p>,
    
            <h2 key="step5-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                Step V: Architecture
            </h2>,
    
            <p key="step5-p1" className="mb-4">
                This brings us to the highest peak, the rarest form of vision, <strong>Architecture</strong>. Architects of perception do not merely adapt to reality or dream about alternative ones. <strong>They reorganize reality permanently.</strong>
            </p>,
    
            <p key="step5-p2" className="mb-4">
                Consider the constructs that dictate our lives today :
            </p>,
    
            <ul key="step5-list" className="list-disc list-inside mb-6 space-y-2 text-blue-200">
                <li><strong>Money</strong></li>
                <li><strong>Democracy</strong></li>
                <li><strong>The Scientific Method</strong></li>
                <li><strong>The Internet</strong></li>
            </ul>,
    
            <p key="step5-p3" className="mb-8">
                None of these existed in nature. They came into being because someone perceived that human experience could be arranged in an entirely new way. Every civilization is simply the accumulated architecture of these perceptual leaps. The future isn't something waiting to be discovered. <strong>It is designed.</strong>
            </p>,
    
            <h2 key="ladder-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                The Invisible Ladder
            </h2>,
    
            <p key="ladder-p1" className="mb-6">
                Most people spend their entire lives climbing career ladders, completely unaware of the ladder of perception.
            </p>,
    
            <p key="ladder-p2" className="mb-8">
                Yet every monumental leap in human history, from fire to language to mathematics to artificial intelligence, it did not occur because the physical world suddenly changed. It occurred because <strong>the observer changed.</strong> Someone simply decided to look one layer deeper than everyone else.
            </p>,
    
            <h2 key="epilogue-title" className="text-2xl font-semibold text-blue-300 mt-10 mb-4">
                Conclusion
            </h2>,
    
            <p key="epilogue-p1" className="mb-4">
                This is why wisdom remains so exceptionally rare. Knowledge merely fills the mind, but perception reshapes it entirely :
            </p>,
    
            <ul key="epilogue-list" className="list-disc list-inside mb-6 space-y-2 text-blue-200">
                <li>
                    The person who <strong>observes</strong> becomes <em>informed</em>.
                </li>
                <li>
                    The person who <strong>recognizes patterns</strong> becomes <em>insightful</em>.
                </li>
                <li>
                    The person who <strong>understands structures</strong> becomes <em>dangerous</em>.
                </li>
                <li>
                    The person who <strong>imagines possibilities</strong> becomes <em>revolutionary</em>.
                </li>
                <li>
                    But the person who <strong>architects reality</strong>... <em>becomes history.</em>
                </li>
            </ul>,
    
            <p key="epilogue-p2" className="mb-6">
                Civilizations are never built by those who merely accept the world as given. They are built by those who first learned to <strong>perceive it differently.</strong>
            </p>
        ],
        image: "/Blog_03_Perception.jpg",
    },
];